import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { formatMonthKeyLabel, isValidMonthKey } from '../../utils/loanAmortization';
import { GlassButton } from '../ui/GlassButton';

export type LoanStartMonthFieldProps = {
  value: string | null;
  onChange: (next: string | null) => void;
};

const MIN_MONTH = '1970-01';

function maxMonth(): string {
  return `${new Date().getFullYear() + 10}-12`;
}

export function LoanStartMonthField({ value, onChange }: LoanStartMonthFieldProps) {
  const hasValue = isValidMonthKey(value);

  return (
    <View style={styles.row}>
      <View style={styles.labelBlock}>
        <Text style={styles.label}>Start month</Text>
        <Text style={styles.hint}>
          {hasValue
            ? 'First payment month — schedule dates follow this'
            : 'Optional. Leave empty until you pick a month'}
        </Text>
      </View>
      <View style={styles.controls}>
        <View style={styles.field}>
          {React.createElement('input', {
            type: 'month',
            value: hasValue ? value : '',
            min: MIN_MONTH,
            max: maxMonth(),
            'aria-label': 'Loan start month',
            onChange: (event: { target: { value: string } }) => {
              const next = event.target.value;
              onChange(isValidMonthKey(next) ? next : null);
            },
            style: {
              width: '100%',
              height: 42,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: hasValue ? colors.text : colors.textMuted,
              fontSize: 18,
              fontWeight: 600,
              textAlign: 'center',
              fontVariantNumeric: 'tabular-nums',
              colorScheme: 'dark',
              cursor: 'pointer',
            },
          })}
        </View>
        {hasValue ? (
          <GlassButton
            variant="ghost"
            size="sm"
            onPress={() => onChange(null)}
            accessibilityLabel="Clear start month"
          >
            Clear
          </GlassButton>
        ) : null}
      </View>
      {!hasValue ? (
        <Text style={styles.emptyNote}>No month selected — we won’t assume this month.</Text>
      ) : (
        <Text style={styles.selectedNote}>{formatMonthKeyLabel(value!)}</Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  labelBlock: {
    gap: 2,
  },
  label: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  field: {
    flex: 1,
    minHeight: 44,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surfaceElevated,
    justifyContent: 'center',
    cursor: 'pointer',
  },
  emptyNote: {
    ...typography.caption,
    color: colors.textMuted,
  },
  selectedNote: {
    ...typography.caption,
    color: colors.accent,
    fontVariant: ['tabular-nums'],
  },
});
